import { useQuery } from '@tanstack/react-query';
import { getServiceDiagnostics } from '@/lib/api';
import { cn } from '@/lib/utils';

const MAX_ATTEMPTS = 6;

// Causa que o operador resolve mexendo em algo (nó, rede, imagem) versus
// estado transitório, que só pede espera.
const TRANSIENT = new Set([
  'updating',
  'pending-schedule',
  'over-provisioned',
  'none',
]);

function fmtWhen(iso?: string): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="px-4 pt-2.5 pb-1 font-mono text-[9.5px] font-bold tracking-[.08em] uppercase text-textMute">
      {children}
    </div>
  );
}

export function ServiceDiagnosis({
  slug,
  serviceId,
}: {
  slug: string;
  serviceId: string;
}) {
  const q = useQuery({
    queryKey: ['service-diagnostics', slug, serviceId],
    queryFn: () => getServiceDiagnostics(slug, serviceId),
    staleTime: 15_000,
  });

  if (q.isPending) {
    return (
      <div className="flex-shrink-0">
        <SectionTitle>Motivo</SectionTitle>
        <p className="px-4 pb-3 text-[11px] text-textMute">diagnosticando…</p>
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="flex-shrink-0">
        <SectionTitle>Motivo</SectionTitle>
        <p className="px-4 pb-3 text-[11px] text-textMute">
          Não foi possível diagnosticar:{' '}
          {(q.error as Error)?.message ?? 'agent não respondeu'}
        </p>
      </div>
    );
  }

  const { cause, summary, detail, hostname, at } = q.data;
  const attempts = (q.data.attempts ?? []).slice(0, MAX_ATTEMPTS);
  const hidden = (q.data.attempts ?? []).length - attempts.length;
  const transient = TRANSIENT.has(cause) || cause === 'unsupported';

  return (
    <div className="flex-shrink-0" data-testid="service-diagnosis">
      <SectionTitle>Motivo</SectionTitle>
      <div className="px-4 pb-3">
        <p
          className={cn(
            'text-[12px] font-semibold leading-snug',
            transient ? 'text-textMute' : 'text-danger'
          )}
        >
          {summary}
        </p>
        {(hostname || at) && (
          <p className="mt-0.5 font-mono text-[9.5px] text-textMute">
            {[hostname, fmtWhen(at)].filter(Boolean).join(' · ')}
          </p>
        )}
        {detail && (
          <p className="mt-1.5 break-words rounded bg-surfaceAlt px-2 py-1.5 font-mono text-[10px] leading-snug text-textMute">
            {detail}
          </p>
        )}
      </div>

      {attempts.length > 0 && (
        <>
          <SectionTitle>Tentativas recentes</SectionTitle>
          <div className="pb-1">
            {attempts.map(a => (
              <div
                key={a.taskID}
                className="border-t border-borderSoft px-4 py-1.5 text-[10.5px]"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-danger">{a.state}</span>
                  <span className="truncate font-mono text-[9.5px] text-textMute">
                    {a.hostname || a.nodeID}
                  </span>
                  <span className="whitespace-nowrap font-mono text-[9.5px] text-textMute">
                    {fmtWhen(a.at)}
                  </span>
                </div>
                {a.message && (
                  <p className="mt-0.5 break-words font-mono text-[9.5px] leading-snug text-textMute">
                    {a.message}
                  </p>
                )}
              </div>
            ))}
            {hidden > 0 && (
              <p className="px-4 py-1.5 text-[10px] text-textMute">
                +{hidden} tentativa(s) mais antiga(s)
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

ServiceDiagnosis.displayName = 'ServiceDiagnosis';

export default ServiceDiagnosis;
